import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { GameSnapshot } from "../types";
import {
  createReturnBriefingReadSession,
  hasProvenActiveHeartFront,
  isReturnBriefingEligible,
  type ReturnBriefingApi,
  type ReturnBriefingSignal,
} from "../lib/return-briefing";

type SourceStatus = "idle" | "loading" | "ready" | "omitted" | "error";

const NEUTRAL_MESSAGE = "Aucun état fiable à présenter pour l'instant.";

export interface ReturnBriefingProps {
  api: ReturnBriefingApi;
  snapshot: GameSnapshot;
}

function sourceStatusMessage(
  frontStatus: SourceStatus,
  chronicleStatus: SourceStatus,
): string | undefined {
  const unavailable: string[] = [];
  if (frontStatus === "error") unavailable.push("Front indisponible.");
  if (chronicleStatus === "error") unavailable.push("Chronique indisponible.");
  return unavailable.length > 0 ? unavailable.join(" ") : undefined;
}

export function ReturnBriefing({ api, snapshot }: ReturnBriefingProps) {
  const eligible = isReturnBriefingEligible(snapshot);
  const [open, setOpen] = useState(false);
  const [bastion, setBastion] = useState<ReturnBriefingSignal>();
  const [front, setFront] = useState<ReturnBriefingSignal>();
  const [chronicle, setChronicle] = useState<ReturnBriefingSignal>();
  const [frontStatus, setFrontStatus] = useState<SourceStatus>("idle");
  const [chronicleStatus, setChronicleStatus] = useState<SourceStatus>("idle");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const mountedRef = useRef(false);
  const readStartedRef = useRef(false);
  const id = useId();
  const panelId = `${id}-panel`;
  const titleId = `${id}-title`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const beginRead = useCallback(() => {
    if (readStartedRef.current) return;
    readStartedRef.current = true;

    if (!isReturnBriefingEligible(snapshot)) return;
    const batch = createReturnBriefingReadSession(api, snapshot).start();
    if (!batch) return;
    setBastion(batch.bastion);
    setFrontStatus(hasProvenActiveHeartFront(snapshot) ? "loading" : "omitted");
    setChronicleStatus("loading");

    // The read session caches this first batch: closing and reopening never
    // creates a second read, while the two public sources settle independently.
    void batch.front
      .then((signal) => {
        if (!mountedRef.current) return;
        setFront(signal);
        setFrontStatus(signal ? "ready" : hasProvenActiveHeartFront(snapshot) ? "error" : "omitted");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setFront(undefined);
        setFrontStatus("error");
      });

    void batch.chronicle
      .then((signal) => {
        if (!mountedRef.current) return;
        setChronicle(signal);
        setChronicleStatus(signal ? "ready" : "error");
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setChronicle(undefined);
        setChronicleStatus("error");
      });
  }, [api, snapshot]);

  useEffect(() => {
    if (open) titleRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (open) beginRead();
  }, [beginRead, open]);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  if (!eligible) return null;

  const signals = [bastion, front, chronicle].filter(
    (signal): signal is ReturnBriefingSignal => signal !== undefined,
  );
  const loading = frontStatus === "loading" || chronicleStatus === "loading";
  const statusMessage = sourceStatusMessage(frontStatus, chronicleStatus);
  const neutral = readStartedRef.current && !loading && signals.length === 0 && !statusMessage;

  return (
    <aside
      className={open ? "return-briefing return-briefing--open" : "return-briefing"}
      aria-label="Le Fil des braises"
    >
      <button
        ref={triggerRef}
        className="return-briefing-launcher"
        type="button"
        aria-controls={panelId}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        Ouvrir Le Fil des braises
      </button>

      {open && (
        <section
          id={panelId}
          className="return-briefing__panel"
          role="region"
          aria-labelledby={titleId}
          aria-busy={loading}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              close();
            }
          }}
        >
          <div className="return-briefing__header">
            <h2 ref={titleRef} id={titleId} tabIndex={-1}>Le Fil des braises</h2>
            <button
              className="return-briefing__close"
              type="button"
              aria-label="Fermer Le Fil des braises"
              onClick={close}
            >
              Fermer
            </button>
          </div>

          <div className="return-briefing__status" role="status" aria-live="polite">
            {loading ? "Lecture des états publics en cours." : statusMessage}
          </div>

          {signals.length > 0 && (
            <ol className="return-briefing__signals">
              {signals.map((signal) => (
                <li key={signal.kind} data-signal-source={signal.kind}>{signal.text}</li>
              ))}
            </ol>
          )}

          {neutral && <p className="return-briefing__empty">{NEUTRAL_MESSAGE}</p>}
        </section>
      )}
    </aside>
  );
}
